/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import { useCoachPermissions } from 'bundles/ai-course-coach/utils/CoachPermissionWrapper';
import CourseToolBar from 'bundles/course-v2/components/navigation/CourseToolBar/CourseToolBar';
import SplitPanelResizeHandle from 'bundles/course-v2/components/navigation/CourseToolBar/SplitPanelResizeHandle';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';

const styles = {
  coachPanelOverrides: css`
    padding-left: var(--cds-spacing-200);
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
};

const WithResizableCourseToolBarWrapper = ({ children }: React.PropsWithChildren<{}>) => {
  const { showEmbeddedCoach } = useCoachPermissions();
  const isScreenXlUp = useMediaQuery(breakpoints.up('xl'));
  const isScreenLgUp = useMediaQuery(breakpoints.up('lg'));
  const isScreenMdUp = useMediaQuery(breakpoints.up('md'));
  const { plugins, expanded } = useCourseToolBarPlugins();
  /* autoClose prevents the toolbar from being open and empty if toolbar is expanded on other pages,
   * but Coach (or other tool) is not available on the current page.
   * autoClose has timing built in to keep the toolbar open while the page is loading. If the page loads without tools,
   * it will close the course tool bar to avoid having an empty toolbar */
  const autoCloseTimer = useRef<null | NodeJS.Timeout>(null);
  const [autoClose, setAutoClose] = useState(false);
  const shouldShowToolbar = showEmbeddedCoach && expanded && !autoClose;
  const shouldShowResizableToolbar = isScreenMdUp && shouldShowToolbar;

  const handleAutoClose = React.useCallback<() => void>(() => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
    }

    autoCloseTimer.current = setTimeout(() => {
      setAutoClose(plugins.length === 0);
    }, 500);
  }, [plugins.length]);

  useEffect(() => {
    handleAutoClose();
  }, [handleAutoClose]);

  const panelSize = useMemo(() => {
    if (isScreenXlUp) {
      return 25;
    } else if (isScreenLgUp) {
      return 30;
    } else if (isScreenMdUp) {
      return 40;
    } else {
      return 50;
    }
  }, [isScreenXlUp, isScreenLgUp, isScreenMdUp]);

  /*
   * if/else conditional return would have been neater here but it causes the entire component tree
   * (more noticeable with video players) to unmount and remount when toggling toolbar states.
   * So using single return with conditional rendering to prevent video playback glitches by avoiding
   * unnecessary remounts of children components, though that means children components
   * are always inside PanelGroup.
   */
  return (
    <PanelGroup direction="horizontal">
      {/* I had to use inline style attribute because emotion css didn't work as expected. The main page content wasn't scrollable */}
      <Panel style={{ overflow: 'auto' }}>{children}</Panel>

      {shouldShowResizableToolbar && <SplitPanelResizeHandle />}

      {shouldShowToolbar && (
        <Panel
          css={!shouldShowResizableToolbar && styles.coachPanelOverrides}
          key={'panel-size-' + panelSize}
          minSize={panelSize}
          defaultSize={panelSize}
          maxSize={50}
        >
          <CourseToolBar showToolBar={false} />
        </Panel>
      )}
    </PanelGroup>
  );
};

export default WithResizableCourseToolBarWrapper;
